import { ref } from 'vue';
import {
  collection,
  addDoc,
  getDocs,
  doc,
  deleteDoc,
  updateDoc,
  orderBy,
  query,
  Timestamp,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import { useAuth } from './useAuth';
import { useToast } from './useToast';
import { extractPlainText, sanitizeRichHtml } from '@/utils/sanitizeHtml';

export interface SchoolNewsItem {
  id: string;
  title: string;
  content: string;
  createdAt: Date;
  author: string;
  featured?: boolean;
  categories?: string[];
}

const schoolNews = ref<SchoolNewsItem[]>([]);
const loading = ref(false);

export function useSchoolNews() {
  const { user } = useAuth();
  const { success, error } = useToast();

  const schoolNewsCollection = collection(db, 'schoolNews');

  const fetchSchoolNews = async (): Promise<void> => {
    try {
      loading.value = true;
      const q = query(schoolNewsCollection, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);

      schoolNews.value = querySnapshot.docs.map((docSnap: QueryDocumentSnapshot<DocumentData>) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          title: data.title,
          content: data.content,
          createdAt: data.createdAt?.toDate() || new Date(),
          author: data.author,
          featured: data.featured || false,
          categories: data.categories || [],
        };
      });
    } catch (err) {
      console.error('Error fetching school news:', err);
      error('Error al cargar las noticias de la escuela', 'Error');
    } finally {
      loading.value = false;
    }
  };

  const createSchoolNews = async (title: string, content: string, createdAt?: Date): Promise<boolean> => {
    if (!user.value) {
      error('Debes estar autenticado para crear noticias', 'Error de autenticación');
      return false;
    }

    if (!title.trim() || !content.trim()) {
      error('El título y contenido son obligatorios', 'Campos requeridos');
      return false;
    }

    try {
      const sanitizedContent = sanitizeRichHtml(content.trim());
      if (!extractPlainText(sanitizedContent)) {
        error('El contenido no puede estar vacío', 'Campos requeridos');
        return false;
      }

      loading.value = true;
      await addDoc(schoolNewsCollection, {
        title: title.trim(),
        content: sanitizedContent,
        createdAt: createdAt ? Timestamp.fromDate(createdAt) : Timestamp.now(),
        author: user.value.displayName?.trim() || 'Administración',
      });

      success('Noticia de la escuela creada correctamente', 'Éxito');
      await fetchSchoolNews();
      return true;
    } catch (err) {
      console.error('Error creating school news:', err);
      error('Error al crear la noticia de la escuela', 'Error');
      return false;
    } finally {
      loading.value = false;
    }
  };

  const updateSchoolNews = async (id: string, title: string, content: string, createdAt?: Date): Promise<boolean> => {
    if (!user.value) {
      error('Debes estar autenticado para editar noticias', 'Error de autenticación');
      return false;
    }

    if (!title.trim() || !content.trim()) {
      error('El título y contenido son obligatorios', 'Campos requeridos');
      return false;
    }

    try {
      const sanitizedContent = sanitizeRichHtml(content.trim());
      if (!extractPlainText(sanitizedContent)) {
        error('El contenido no puede estar vacío', 'Campos requeridos');
        return false;
      }

      loading.value = true;
      const newsDoc = doc(db, 'schoolNews', id);
      const updateData: DocumentData = {
        title: title.trim(),
        content: sanitizedContent,
      };

      if (createdAt) {
        updateData.createdAt = Timestamp.fromDate(createdAt);
      }

      await updateDoc(newsDoc, updateData);

      success('Noticia de la escuela actualizada correctamente', 'Éxito');
      await fetchSchoolNews();
      return true;
    } catch (err) {
      console.error('Error updating school news:', err);
      error('Error al actualizar la noticia de la escuela', 'Error');
      return false;
    } finally {
      loading.value = false;
    }
  };

  const deleteSchoolNews = async (id: string): Promise<boolean> => {
    if (!user.value) {
      error('Debes estar autenticado para eliminar noticias', 'Error de autenticación');
      return false;
    }

    try {
      loading.value = true;
      await deleteDoc(doc(db, 'schoolNews', id));
      success('Noticia de la escuela eliminada correctamente', 'Éxito');
      await fetchSchoolNews();
      return true;
    } catch (err) {
      console.error('Error deleting school news:', err);
      error('Error al eliminar la noticia de la escuela', 'Error');
      return false;
    } finally {
      loading.value = false;
    }
  };

  return {
    schoolNews,
    loading,
    fetchSchoolNews,
    createSchoolNews,
    updateSchoolNews,
    deleteSchoolNews,
  };
}
